/* eslint-disable camelcase */
import ReactGA from 'react-ga4';

import { isAuthenticated } from 'actions/user';
import {
  // IS_INDIAN_EXCHANGE,
  IS_RN_WEBVIEW,
  NON_KYC_CHECK_DATE,
} from 'constants/constants';
import { getDateTime, getTimestampInMS } from 'helpers/day';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { getHexaDecimalUserId, userCountryVisibilityFilter } from 'helpers/user';
import { isAndroidDevice, isIOSDevice } from 'helpers/utils';
import type { AppliedOffers } from 'types/IOffer';
import type IUser from 'types/IUser';

import { TDesktopCard, TMobileCard } from './types';

interface MobileFilterParams {
  card: TMobileCard;
  isLoggedIn: boolean;
  user: IUser;
  appliedOffers: AppliedOffers;
  activeTheme: string;
}

interface DesktopFilterParams {
  card: TDesktopCard;
  isLoggedIn: boolean;
  user: IUser;
  appliedOffers: AppliedOffers;
  activeTheme: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isIndianExchangeCard = ({ card }: { card: TDesktopCard | TMobileCard }) => {
  // TODO: Uncomment this when we have Indian Exchange api fixed
  // return IS_INDIAN_EXCHANGE && card.indianExchangeCard;
  return true;
};

export const loginVisibilityFilter = (
  card: TDesktopCard | TMobileCard,
  isLoggedIn: boolean = false
) => {
  /**
   * showWhenLogin variable is used to indicate to show the card only when user is in either of pre or post login state.
   * So the value can be "preLogin" | "postLogin"
   * If not specified then show in both states.
   */
  if ('showWhenLogin' in card) {
    const { showWhenLogin } = card;
    if (showWhenLogin === 'postLogin') {
      return isLoggedIn === true;
    }
    return isLoggedIn === false;
  }

  return true;
};

export const filterCardByOS = card => {
  if ('deviceOS' in card) {
    if (card.deviceOS === 'ios' && isIOSDevice()) {
      return true;
    }
    if (card.deviceOS === 'android' && isAndroidDevice()) {
      return true;
    }
    return false;
  }
  return true;
};

const kycFilterCard = (
  card: TDesktopCard | TMobileCard,
  user: IUser,
  appliedOffers: AppliedOffers
) => {
  try {
    if (card.type === 'kyc_complete') {
      const isLoggedIn = isAuthenticated(user);
      if (!isLoggedIn) {
        return false;
      }
      const isKycOfferApplied = 'kyc_complete' in appliedOffers;
      const isUserNonIndian =
        user?.country && typeof user.country === 'string'
          ? user.country.toLowerCase() !== 'india'
          : true;
      const isUserRegisteredAfter26April =
        getTimestampInMS(getDateTime(user?.profile?.registration_date)) -
          getTimestampInMS(getDateTime(NON_KYC_CHECK_DATE)) >
        0;
      const shouldRemoveKycOffer =
        isKycOfferApplied ||
        isUserNonIndian ||
        isUserRegisteredAfter26April ||
        user.is_kyc_done;
      return !shouldRemoveKycOffer;
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const mobileViewVisibiltyFilter = (card: TMobileCard) => {
  /**
   * showWhenMobile variable is used to indicate to show the card only in mobile app or browser.
   * So the value can be "browser" | "app"
   * If not specified then show in both.
   */
  if ('showWhenMobile' in card) {
    const { showWhenMobile } = card;
    if (showWhenMobile === 'browser') {
      return Boolean(IS_RN_WEBVIEW) === false;
    }
    return Boolean(IS_RN_WEBVIEW) === true;
  }

  return true;
};

const offerTimelineCardFilter = (card: TDesktopCard | TMobileCard, user: IUser) => {
  if (card.type === 'offerTimeline') {
    if ('is_sub_account' in user) {
      const { is_sub_account } = user;
      return !is_sub_account;
    }
  }

  return true;
};

/**
 * @description - Filter cards based on selected theme by the user.
 * If no theme field is found on card then show card irrespective of the selected them.
 */
const themeVisibilityFilter = ({
  card,
  activeTheme,
}: {
  card: TDesktopCard | TMobileCard;
  activeTheme: string;
}) => {
  // If theme field is defined
  if ('theme' in card && typeof card.theme === 'string') {
    return card.theme === activeTheme;
  }

  // If theme field is not present then card needs to be rendered always.
  return true;
};

/**
 *
 * @param cardName - Name which needs to be reported to Mixpanel and Google Analytics.
 */
export const trackCardClicks = (cardName: string) => {
  trackMixpanelEvent('Offer Card Clicked', {
    card_name: cardName,
    page_name: 'All Markets Page',
  });

  ReactGA.event({
    category: 'User',
    action: 'AM_Banner_Cards',
    label: cardName,
  });
};

/** @description - if list of userIds given for card data, match user id for rendering */
const userIdsFilter = (card: TDesktopCard | TMobileCard, id: IUser['id']) =>
  Array.isArray(card.meta?.ids)
    ? card.meta?.ids.includes(getHexaDecimalUserId(id))
    : true;

export const desktopFilters = ({
  card,
  isLoggedIn = false,
  user,
  appliedOffers,
  activeTheme,
}: DesktopFilterParams) => {
  return (
    loginVisibilityFilter(card, isLoggedIn) &&
    kycFilterCard(card, user, appliedOffers) &&
    offerTimelineCardFilter(card, user) &&
    userCountryVisibilityFilter(user.country, card.countries) &&
    themeVisibilityFilter({ card, activeTheme }) &&
    isIndianExchangeCard({ card }) &&
    userIdsFilter(card, user.id)
  );
};

export const mobileFilters = ({
  card,
  isLoggedIn = false,
  user,
  appliedOffers,
  activeTheme,
}: MobileFilterParams) => {
  return (
    loginVisibilityFilter(card, isLoggedIn) &&
    mobileViewVisibiltyFilter(card) &&
    kycFilterCard(card, user, appliedOffers) &&
    offerTimelineCardFilter(card, user) &&
    isIndianExchangeCard({ card }) &&
    userCountryVisibilityFilter(user.country, card.countries) &&
    themeVisibilityFilter({ card, activeTheme }) &&
    userIdsFilter(card, user.id) &&
    filterCardByOS(card)
  );
};
